/**
 * Client-side image compression before Cloudinary upload.
 * Saves mobile bandwidth: a raw 10MB iPhone photo becomes ~300-800KB
 * before it leaves the device. Cloudinary still applies f_auto/q_auto on delivery.
 */

const HEIC_TYPES = ["image/heic", "image/heif"];

export async function compressImage(file, { maxDim = 1600, quality = 0.85, mime = "image/jpeg" } = {}) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // HEIC/HEIF: browser can't decode natively — skip local compress, send original to server
  if (HEIC_TYPES.includes(file.type.toLowerCase())) return file;
  if (file.size < 200 * 1024) return file; // skip small files — not worth it

  try {
    const img = await loadImage(file);
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
    revokeImage(img);
    if (!blob || blob.size >= file.size) return file; // no gain, return original

    const newName = file.name.replace(/\.(png|webp|heic|heif|bmp|tiff)$/i, ".jpg");
    return new File([blob], newName, { type: mime, lastModified: Date.now() });
  } catch {
    return file;
  }
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { img._objectURL = url; resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function revokeImage(img) {
  if (img && img._objectURL) URL.revokeObjectURL(img._objectURL);
}
